import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CustomCart } from '@cart/const';
import {
  Address,
  API_ENDPOINTS,
  OccCustomerConsignment,
  PaymentMode,
  PaymentModeList,
  PlaceOrderParams,
  ReceiptType,
  ReceiptTypeList,
  Service,
  TimeSlot,
  TimeSlotsApiParams,
} from '@checkout-v2/const';
import { CustomAddress, HTTP_CONTEXT_ENDPOINT } from '@core/const';
import { LocalDate, SHOPS_NORMALIZER } from '@core/utils';
import { CustomOrder } from '@order/const';
import { Pos, ShopsPayload } from '@shop/const';
import { ConverterService, Occ, OccEndpointsService, ORDER_NORMALIZER } from '@spartacus/core';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CheckoutApiService {
  constructor(
    private http: HttpClient,
    private occEndpointsService: OccEndpointsService,
    private converterService: ConverterService,
  ) {}

  /**
   * Загрузить способы получения товара
   */
  loadReceiptTypes(cartId: string): Observable<ReceiptType[]> {
    const url = this.occEndpointsService.buildUrl(API_ENDPOINTS.receiptTypes, {
      urlParams: { cartId },
    });

    return this.http
      .get<ReceiptTypeList>(url, {
        context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.receiptTypes),
      })
      .pipe(map((res) => res.deliveryModes ?? []));
  }

  /**
   * Загрузить список сохраненных адресов доставки пользователя
   */
  loadDeliveryAddressList(cartGuid: string): Observable<Address[]> {
    const url = this.occEndpointsService.buildUrl(API_ENDPOINTS.addresses, {
      queryParams: { cartGuid },
    });

    return this.http
      .get<{ addresses: Address[] }>(url, {
        context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.addresses),
      })
      .pipe(map((res) => (res.addresses ? res.addresses.reverse() : [])));
  }

  /**
   * Сохранить адрес доставки
   */
  saveDeliveryAddress(cartId: string, address: Address): Observable<CustomAddress> {
    const url = this.occEndpointsService.buildUrl(API_ENDPOINTS.addressDelivery, {
      urlParams: { cartId },
    });

    return this.http.post<CustomAddress>(url, address, {
      context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.addressDelivery),
    });
  }

  /**
   * Изменить адрес доставки
   */
  editDeliveryAddress(cartId: string, address: Address): Observable<null> {
    const url = this.occEndpointsService.buildUrl(API_ENDPOINTS.address, {
      urlParams: {
        addressId: address.id,
      },
      queryParams: {
        cartGuid: cartId,
      },
    });

    return this.http.put<null>(url, address, {
      context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.address),
    });
  }

  /**
   * Удалить адрес доставки
   */
  deleteDeliveryAddress(cartId: string, addressId: string): Observable<CustomAddress> {
    const url = this.occEndpointsService.buildUrl(API_ENDPOINTS.address, {
      urlParams: {
        addressId,
      },
      queryParams: {
        cartGuid: cartId,
      },
    });

    return this.http.delete<CustomAddress>(url, {
      context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.address),
    });
  }

  /**
   * Загрузить список сохраненных магазинов самовывоза пользователя
   */
  loadUserShopList(): Observable<Pos[]> {
    const url = this.occEndpointsService.buildUrl(API_ENDPOINTS.userShopList, {
      queryParams: { fields: 'FULL' },
    });

    return this.http
      .get<ShopsPayload>(url, {
        context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.userShopList),
      })
      .pipe(
        this.converterService.pipeable(SHOPS_NORMALIZER),
        map((payload: ShopsPayload) => payload?.pointOfServices ?? []),
      );
  }

  /**
   * Выбрать магазин самовывоза
   */
  addUserShop(shopName: string): Observable<Pos[]> {
    const url = this.occEndpointsService.buildUrl(API_ENDPOINTS.userShop, {
      urlParams: { shopName },
      queryParams: { fields: 'FULL' },
    });

    return this.http
      .post<ShopsPayload>(
        url,
        {},
        {
          context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.userShop),
        },
      )
      .pipe(
        this.converterService.pipeable(SHOPS_NORMALIZER),
        map((payload: ShopsPayload) => payload?.pointOfServices ?? []),
      );
  }

  /**
   * Удалить магазин самовывоза
   */
  removeUserShop(shopName: string): Observable<Pos[]> {
    const url = this.occEndpointsService.buildUrl(API_ENDPOINTS.userShop, {
      urlParams: { shopName },
      queryParams: { fields: 'FULL' },
    });

    return this.http
      .delete<ShopsPayload>(url, {
        context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.userShop),
      })
      .pipe(
        this.converterService.pipeable(SHOPS_NORMALIZER),
        map((payload: ShopsPayload) => payload?.pointOfServices ?? []),
      );
  }

  /**
   * Загрузить услуги
   */
  loadServices(cartId: string): Observable<Service[]> {
    const url = this.occEndpointsService.buildUrl(API_ENDPOINTS.services, {
      urlParams: {
        cartId,
      },
    });

    return this.http
      .post<{ services: Service[] }>(
        url,
        {},
        {
          context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.services),
        },
      )
      .pipe(map((payload) => payload.services));
  }

  /**
   * Загрузить таймслоты
   */
  loadTimeSlots(cartId: string, params: TimeSlotsApiParams): Observable<TimeSlot[]> {
    const url = this.occEndpointsService.buildUrl(API_ENDPOINTS.timeSlots, {
      urlParams: {
        cartId,
      },
      queryParams: {
        transportIds: params.transportIds.join(','),
        dateFrom: params.dateFrom ?? LocalDate.toISOString().split('T')[0],
        dateTo: params.dateTo,
        fields: 'DEFAULT,slots(groups(options(interval(clientInterval(timeFromTo)))))',
      },
    });

    return this.http
      .post<{ slots: TimeSlot[] }>(
        url,
        {},
        {
          context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.timeSlots),
        },
      )
      .pipe(map((payload) => payload.slots));
  }

  /**
   * Загрузить способы оплаты
   */
  loadPaymentModes(cartId: string): Observable<PaymentMode[]> {
    const url = this.occEndpointsService.buildUrl(API_ENDPOINTS.paymentModes, {
      urlParams: {
        cartId,
      },
    });

    return this.http
      .get<PaymentModeList>(url, {
        context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.paymentModes),
      })
      .pipe(map((res) => res?.paymentModes ?? []));
  }

  /**
   * Выбрать способ оплаты
   */
  setPaymentMode(cartId: string, paymentModeId: string): Observable<unknown> {
    const url = this.occEndpointsService.buildUrl(API_ENDPOINTS.paymentMode, {
      urlParams: {
        cartId,
      },
    });

    return this.http.put(
      url,
      {},
      {
        params: { paymentModeId, refreshCart: true },
        context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.paymentMode),
      },
    );
  }

  /**
   * Сбросить выбранный способ оплаты
   */
  resetPaymentMode(cartId: string): Observable<unknown> {
    const url = this.occEndpointsService.buildUrl(API_ENDPOINTS.paymentMode, {
      urlParams: {
        cartId,
      },
    });

    return this.http.delete(url, {
      context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.paymentMode),
    });
  }

  /**
   * Установить выбранные данные пользователя
   */
  setCheckoutData(cartId: string, checkoutData: OccCustomerConsignment): Observable<CustomCart> {
    const url = this.occEndpointsService.buildUrl(API_ENDPOINTS.customerConsignment, {
      urlParams: { cartId },
    });

    return this.http.put<CustomCart>(url, checkoutData, {
      context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.customerConsignment),
    }) as Observable<CustomCart>;
  }

  /**
   * Оформить заказ
   */
  placeOrder({
    cartId,
    customerOrganizationUid,
    customerConsignment,
    checkoutContactFormData,
    promoCampaignParams,
  }: PlaceOrderParams): Observable<CustomOrder> {
    const url = this.occEndpointsService.buildUrl(API_ENDPOINTS.placeOrder, {
      queryParams: { fields: 'FULL' },
    });

    return this.http
      .post<Occ.Order>(
        url,
        {
          cartId,
          customerOrganizationUid,
          termsChecked: true,
          customerConsignment,
          ...checkoutContactFormData,
          ...promoCampaignParams,
        },
        {
          context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.placeOrder),
        },
      )
      .pipe(this.converterService.pipeable(ORDER_NORMALIZER));
  }
}
